import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useMessages } from "../../../hooks/messages/useMessages";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, MoreVertical, UserX } from "lucide-react";
import { setActiveConversation, resetNotifications, setNotifications } from "../../../slices/messagesSlice";
import ChatMessage from "../../../components/ChatComponents/ChatMessage/ChatMessage";
import ChatInput from "../../../components/ChatComponents/ChatInput/ChatInput";
import PostContextCard from "../../../components/ChatComponents/PostContextCard/PostContextCard";
import AnimatedLoadingScreen from "../../../components/UIFeedback/AnimatedLoadingScreen/AnimatedLoadingScreen";
import BlockUserConfirmation from "../../../components/BlockUserConfirmation";
import socket from "../../../services/socketClient";

const Chat = ({ chat, onBack }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { messages, messagesLoading, sendMessage } = useMessages(chat._id);
  const user = useSelector((state) => state.auth.user);
  const authenticatedUserId = user ? user._id : null;
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const [showBlockConfirmation, setShowBlockConfirmation] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const headerMenuRef = useRef(null);
  const headerMenuButtonRef = useRef(null);

  // Scroll to the bottom of the messages within the container
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!authenticatedUserId) return;
    dispatch(setActiveConversation(chat._id));
    dispatch(resetNotifications(chat._id));
  
    socket.emit("joinChat", { userId: authenticatedUserId, conversationId: chat._id });
    socket.emit("readMessages", { conversationId: chat._id, userId: authenticatedUserId });
  
    // Ensure Redux notifications are cleared immediately
    socket.on("updateConversation", (conversation) => {
      dispatch(setNotifications([{ _id: conversation._id, unreadCount: 0 }]));
    });
  
    return () => {
      socket.emit("leaveChat", { userId: authenticatedUserId, conversationId: chat._id });
      socket.off("updateConversation");
    };
  }, [chat._id, authenticatedUserId, dispatch]);

  useEffect(() => {
    if (!showHeaderMenu) return;

    const handleClickOutside = (event) => {
      const isOutsideButton =
        headerMenuButtonRef.current && !headerMenuButtonRef.current.contains(event.target);
      const isOutsideMenu =
        headerMenuRef.current && !headerMenuRef.current.contains(event.target);
      if (isOutsideButton && isOutsideMenu) {
        setShowHeaderMenu(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowHeaderMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showHeaderMenu]);

  useEffect(() => {
    setShowHeaderMenu(false);
  }, [chat._id]);

  if (!user) {
    return (
      <div className="flex flex-col h-[85vh] max-w-lg w-full mx-auto mt-16 bg-background">
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-muted-foreground">{t("login_required_messages")}</p>
        </div>
      </div>
    );
  }

  if (messagesLoading) {
    return <AnimatedLoadingScreen message={t('loading_messages')} />;
  }

  return (
    <div className="flex flex-col h-full max-w-2xl w-full mx-auto bg-background overflow-hidden">
      <div className="relative bg-white border-b border-border px-3 sm:px-4 md:px-6 py-3 sm:py-4 flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={onBack}
            className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-lg hover:bg-muted transition-colors -ml-1"
            aria-label={t('go_back', 'Go Back')}
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-foreground" />
          </button>
          <h2 className="font-semibold text-foreground text-sm sm:text-base md:text-lg">
            {t('chat_with', 'Chat with')} {chat?.participant?.username || t('unknown_user', 'Unknown User')}
          </h2>
          <button
            ref={headerMenuButtonRef}
            onClick={() => setShowHeaderMenu((v) => !v)}
            className="ml-auto flex items-center justify-center w-9 h-9 rounded-lg hover:bg-muted transition-colors"
            aria-label={t('chat_options', 'Chat options')}
            aria-expanded={showHeaderMenu}
            aria-haspopup="true"
          >
            <MoreVertical className="w-5 h-5 text-foreground" />
          </button>
        </div>

        {showHeaderMenu && chat?.participant?._id && (
          <div
            ref={headerMenuRef}
            className="absolute right-3 top-14 bg-white rounded-xl shadow-lg border border-border w-48 overflow-hidden z-30"
            role="menu"
            aria-orientation="vertical"
          >
            <button
              onClick={() => {
                setShowHeaderMenu(false);
                setShowBlockConfirmation(true);
              }}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50"
              role="menuitem"
            >
              <UserX className="w-4 h-4" />
              {t('block_user_action', 'Block @{{username}}', {
                username: chat?.participant?.username || 'user',
              })}
            </button>
          </div>
        )}
      </div>

      {/* Post Context Card */}
      {chat?.postId && (
        <div className="flex-shrink-0">
          <PostContextCard
            postId={chat.postId}
            postType={chat.postType}
            postName={chat.postName}
            postBreed={chat.postBreed}
            postImageUrl={chat.postImageUrl}
            postLocation={chat.postLocation}
            postDate={chat.postDate}
          />
        </div>
      )}

      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto overscroll-contain px-3 sm:px-4 md:px-6 py-4 sm:py-6 space-y-3"
      >
        {messages.length === 0 && (
          <div className="flex items-center justify-center min-h-[200px]">
            <p className="text-muted-foreground text-sm text-center px-4">
              {chat?.postName 
                ? t('no_messages_yet_about', { name: chat.postName, defaultValue: 'No messages yet. Send a message to start the conversation about {{name}}.' })
                : t('no_messages_yet', 'No messages yet. Start the conversation!')}
            </p>
          </div>
        )}
        {messages.map((message) => (
          <ChatMessage
            key={message._id || message.createdAt}
            message={message}
            isMine={message.senderId === authenticatedUserId}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex-shrink-0 px-3 sm:px-4 md:px-6 pt-2" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
        <div className="bg-white border border-[rgb(194,208,205)] rounded-2xl p-2 sm:p-3 shadow-lg">
          <ChatInput
            onSendMessage={(text) =>
              sendMessage(authenticatedUserId, chat.participant._id, chat._id, text)
            }
          />
        </div>
      </div>

      <BlockUserConfirmation
        isOpen={showBlockConfirmation}
        onClose={() => setShowBlockConfirmation(false)}
        userId={chat?.participant?._id}
        username={chat?.participant?.username}
        onBlocked={() => {
          setShowBlockConfirmation(false);
          navigate('/messages');
        }}
      />
    </div>
  );
};

export default Chat;
